import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import Avatar from "@/components/Avatar";
import AdminGroupsFolder from "@/components/AdminGroupsFolder";
import { useAdmin } from "@/contexts/AdminContext";
import { WorldMap } from "react-svg-worldmap";
import {
  Lock, Users, Cake, Image as ImageIcon, MapPin, TrendingUp,
  Loader2, RefreshCw, Calendar, Globe, Truck, Activity, Eye, EyeOff,
  Zap, BellRing, ArrowDown,
} from "lucide-react";

/**
 * /admin/analytics — Born Day investor-grade dashboard.
 * Pulls from GET /api/admin/stats. Admin only.
 */
function StatTile({ icon: Icon, label, value, sub, testId, accent }) {
  return (
    <div className="bd-card" style={{ padding: "0.9rem 1rem" }} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest"
           style={{ color: accent || "var(--text-dim)" }}>
        <Icon size={12} /> {label}
      </div>
      <div className="text-3xl font-extrabold mt-1" style={{ letterSpacing: "-0.04em" }}>
        {value}
      </div>
      {sub && <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>{sub}</div>}
    </div>
  );
}

function SignupSparkline({ data }) {
  if (!data || data.length === 0) {
    return <div className="text-xs" style={{ color: "var(--text-dim)" }}>No signups in the last 30 days yet.</div>;
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-16" data-testid="signup-sparkline">
      {data.map((d) => (
        <div
          key={d.date}
          className="flex-1 rounded-t"
          title={`${d.date} · ${d.count}`}
          style={{
            background: "var(--primary)",
            opacity: 0.85,
            height: `${Math.max(6, (d.count / max) * 100)}%`,
          }}
        />
      ))}
    </div>
  );
}

function CountryBar({ row, max }) {
  const pct = Math.max(4, (row.count / max) * 100);
  return (
    <li className="flex items-center gap-3" data-testid={`country-${row.country}`}>
      <span className="text-sm w-32 truncate">{row.country}</span>
      <div className="flex-1 rounded h-2.5 relative" style={{ background: "var(--bg-soft)" }}>
        <div
          className="absolute top-0 left-0 h-2.5 rounded"
          style={{ width: `${pct}%`, background: "var(--primary)", opacity: 0.85 }}
        />
      </div>
      <span className="bd-mono text-xs w-12 text-right">{row.count}</span>
    </li>
  );
}

export default function AdminAnalytics() {
  const { unlocked } = useAdmin();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // World map starts collapsed — investor-pitch view (heavy-ish, ~50kb of SVG)
  // — and the user clicks to reveal it. Light/dark variant follows
  // the current Born Day theme automatically.
  const [showMap, setShowMap] = useState(false);
  const [mapTheme, setMapTheme] = useState("light");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/admin/stats");
      setData(data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (unlocked) load(); }, [unlocked]);

  if (!unlocked) {
    return (
      <AppShell>
        <div className="bd-surface p-6 max-w-lg" data-testid="admin-analytics-locked">
          <div className="flex items-center gap-2 font-bold text-lg mb-2">
            <Lock size={16} /> Locked
          </div>
          <p style={{ color: "var(--text-dim)" }}>
            Tap the <strong>Admin</strong> pill at the bottom-left and enter the passcode.
          </p>
        </div>
      </AppShell>
    );
  }

  const t = data?.totals || {};
  const e = data?.engagement || {};
  const f = data?.funnel || {};
  const profileCompletion = t.users ? Math.round((t.with_avatar / t.users) * 100) : 0;
  const birthdayShare = t.users ? Math.round((t.with_birthday / t.users) * 100) : 0;
  const maxCountry = Math.max(1, ...(data?.by_country || []).map((r) => r.count));

  // react-svg-worldmap accepts [{country: ISO2, value: n}]. Map our
  // by_country names to ISO2 codes. Anything unmatched silently drops
  // off the map but still appears in the bar list below it.
  const COUNTRY_ISO = {
    "united states": "us", "usa": "us", "us": "us",
    "united kingdom": "gb", "uk": "gb",
    "canada": "ca", "mexico": "mx", "brazil": "br", "argentina": "ar",
    "chile": "cl", "peru": "pe", "colombia": "co", "venezuela": "ve",
    "france": "fr", "germany": "de", "italy": "it", "spain": "es",
    "portugal": "pt", "netherlands": "nl", "belgium": "be", "ireland": "ie",
    "switzerland": "ch", "austria": "at", "sweden": "se", "norway": "no",
    "denmark": "dk", "finland": "fi", "poland": "pl", "russia": "ru",
    "ukraine": "ua", "greece": "gr", "turkey": "tr", "israel": "il",
    "saudi arabia": "sa", "united arab emirates": "ae", "uae": "ae",
    "india": "in", "pakistan": "pk", "bangladesh": "bd", "china": "cn",
    "japan": "jp", "south korea": "kr", "korea": "kr", "thailand": "th",
    "vietnam": "vn", "philippines": "ph", "indonesia": "id", "malaysia": "my",
    "singapore": "sg", "australia": "au", "new zealand": "nz",
    "south africa": "za", "nigeria": "ng", "kenya": "ke", "egypt": "eg",
    "morocco": "ma", "ghana": "gh",
  };
  const mapData = (data?.by_country || [])
    .map((row) => {
      const iso = COUNTRY_ISO[(row.country || "").toLowerCase()];
      return iso ? { country: iso, value: row.count } : null;
    })
    .filter(Boolean);

  return (
    <AppShell>
      <div className="flex flex-col gap-6 max-w-6xl">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
              Back Office · Analytics
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold" style={{ letterSpacing: "-0.04em" }}>
              Born Day on a global scale.
            </h1>
            <p style={{ color: "var(--text-dim)" }} className="mt-1">
              Who&apos;s signed up, who has a real profile, who&apos;s added a birthday, and where they are on the planet.
            </p>
          </div>
          <button className="bd-btn" onClick={load} disabled={busy} data-testid="admin-analytics-refresh">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <><RefreshCw size={14} /> Refresh</>}
          </button>
        </div>

        {err && (
          <div className="text-sm px-3 py-2 rounded"
               style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
               data-testid="admin-analytics-error">
            {err}
          </div>
        )}

        {!data && !err && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-dim)" }}>
            <Loader2 size={14} className="animate-spin" /> Loading global stats…
          </div>
        )}

        {data && (
          <>
            {/* ──────────── INVESTOR SNAPSHOT ──────────── */}
            <div data-testid="investor-snapshot">
              <div className="text-xs uppercase tracking-widest mb-2"
                   style={{ color: "var(--primary)" }}>
                Investor snapshot · last {e.dau != null ? "24h / 7d / 30d" : "—"}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile icon={Activity} label="Daily active users"
                  value={e.dau ?? 0}
                  sub={`${e.wau ?? 0} weekly · ${e.mau ?? 0} monthly`}
                  testId="stat-dau" accent="var(--primary)" />
                <StatTile icon={TrendingUp} label="Stickiness · DAU/MAU"
                  value={`${e.stickiness_pct ?? 0}%`}
                  sub="Higher = users come back more often"
                  testId="stat-stickiness" accent="var(--primary)" />
                <StatTile icon={Eye} label="Visits · last 7d"
                  value={e.visits_last_7d ?? 0}
                  sub={`${e.avg_visits_per_active_user_7d ?? 0} avg per active user`}
                  testId="stat-visits-7d" accent="var(--primary)" />
                <StatTile icon={Zap} label="Visits · last 24h"
                  value={e.visits_last_24h ?? 0}
                  sub={`${e.visits_last_30d ?? 0} in the last 30 days`}
                  testId="stat-visits-24h" accent="var(--primary)" />
              </div>
            </div>

            {/* ──────────── CONVERSION FUNNEL ──────────── */}
            <div className="bd-surface p-5" data-testid="conversion-funnel">
              <div className="flex items-center gap-2 mb-4 font-bold text-lg">
                <TrendingUp size={16} /> Conversion funnel
                <span className="text-xs" style={{ color: "var(--text-dim)" }}>
                  · signup → fully engaged
                </span>
              </div>
              {[
                { label: "Signed up",            value: f.signed_up,          pct: 100 },
                { label: "Completed onboarding", value: f.onboarded,          pct: f.onboarded_pct },
                { label: "Added birthday",       value: f.with_birthday,      pct: f.with_birthday_pct },
                { label: "Joined a group",       value: f.in_a_group_count,   pct: f.in_a_group_pct },
                { label: "Created an event",     value: f.created_event_count,pct: f.created_event_pct },
              ].map((step, idx, arr) => (
                <div key={step.label} className="flex flex-col">
                  <div className="flex items-center gap-3">
                    <div
                      className="rounded h-9 flex items-center px-3 text-sm font-bold"
                      style={{
                        width: `${Math.max(15, step.pct ?? 0)}%`,
                        background: "color-mix(in srgb, var(--primary) " + Math.max(30, 100 - idx * 15) + "%, transparent)",
                        color: "var(--primary-fg)",
                        transition: "width 280ms ease",
                      }}
                      data-testid={`funnel-step-${idx}`}
                    >
                      {step.label}
                    </div>
                    <div className="bd-mono text-sm" style={{ minWidth: 110 }}>
                      <strong>{step.value ?? 0}</strong>
                      <span className="ml-1" style={{ color: "var(--text-dim)" }}>
                        ({step.pct ?? 0}%)
                      </span>
                    </div>
                  </div>
                  {idx < arr.length - 1 && (
                    <ArrowDown size={12} className="ml-1 my-0.5" style={{ color: "var(--text-dim)" }} />
                  )}
                </div>
              ))}
            </div>

            {/* ──────────── HEADLINE TOTALS ──────────── */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={Users} label="Total members" value={t.users}
                sub={`+${t.users_last_7d} in 7d · +${t.users_last_30d} in 30d`} testId="stat-users" />
              <StatTile icon={ImageIcon} label="With a profile photo"
                value={t.with_avatar}
                sub={`${profileCompletion}% of all members`} testId="stat-avatars" />
              <StatTile icon={Cake} label="With a birthday set"
                value={t.with_birthday}
                sub={`${birthdayShare}% of all members`} testId="stat-birthdays" />
              <StatTile icon={Truck} label="With shipping address"
                value={t.with_shipping}
                sub="Ready to receive gifts" testId="stat-shipping" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={Calendar} label="Events created" value={t.events_total}
                sub={`+${t.events_last_7d ?? 0} in 7d · ${t.events_birthday} are birthdays`}
                testId="stat-events" />
              <StatTile icon={Users} label="Groups (calendars)" value={t.groups_total}
                sub={`+${t.groups_last_7d ?? 0} in 7d · ${t.groups_scheduling_mode} in scheduling mode`}
                testId="stat-groups" />
              <StatTile icon={BellRing} label="Notifications fired" value={t.notifications_total ?? 0}
                sub={`+${t.notifications_last_7d ?? 0} in 7d`}
                testId="stat-notifications" />
              <div className="bd-card" style={{ padding: "0.9rem 1rem" }} data-testid="stat-spark">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest"
                     style={{ color: "var(--text-dim)" }}>
                  <TrendingUp size={12} /> Signups · last 30d
                </div>
                <div className="mt-2">
                  <SignupSparkline data={data.daily_signups} />
                </div>
              </div>
            </div>

            {/* ──────────── WORLD MAP + COUNTRIES ──────────── */}
            <div className="bd-surface p-5" data-testid="admin-analytics-countries">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2 font-bold text-lg">
                  <Globe size={16} /> World footprint
                  <span className="text-xs font-normal" style={{ color: "var(--text-dim)" }}>
                    · by self-reported shipping country
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="bd-btn bd-btn-ghost"
                    style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                    onClick={() => setShowMap((v) => !v)}
                    data-testid="toggle-world-map"
                  >
                    {showMap ? <><EyeOff size={12} /> Hide map</> : <><Eye size={12} /> Show map</>}
                  </button>
                  {showMap && (
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}
                      onClick={() => setMapTheme((m) => m === "light" ? "dark" : "light")}
                      data-testid="toggle-map-theme"
                      title="Switch map between light and dark"
                    >
                      {mapTheme === "light" ? "🌙 Dark map" : "☀️ Light map"}
                    </button>
                  )}
                </div>
              </div>

              {showMap && (
                <div
                  className="rounded-lg mb-4 overflow-hidden"
                  style={{
                    background: mapTheme === "light" ? "#F4F4F0" : "#0B0B12",
                    padding: "0.6rem",
                    border: "1px solid var(--border)",
                  }}
                  data-testid="world-map-container"
                >
                  {mapData.length === 0 ? (
                    <p className="text-sm py-8 text-center"
                       style={{ color: mapTheme === "light" ? "#666" : "#999" }}>
                      No mapped countries yet. Once members set their country in
                      Profile · Shipping address, dots will light up here.
                    </p>
                  ) : (
                    <WorldMap
                      color={mapTheme === "light" ? "#FF8E72" : "#00E5FF"}
                      backgroundColor={mapTheme === "light" ? "#F4F4F0" : "#0B0B12"}
                      strokeOpacity={0.4}
                      borderColor={mapTheme === "light" ? "#BBB" : "#333"}
                      valueSuffix=" members"
                      tooltipBgColor={mapTheme === "light" ? "#1A1A1A" : "#FFFFFF"}
                      tooltipTextColor={mapTheme === "light" ? "#FFFFFF" : "#1A1A1A"}
                      size="responsive"
                      data={mapData}
                      richInteraction
                    />
                  )}
                </div>
              )}

              {data.by_country.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--text-dim)" }}>
                  No country data yet. Members can add their country in Profile · Shipping address.
                </p>
              ) : (
                <ul className="space-y-2" data-testid="countries-list">
                  {data.by_country.map((row) => (
                    <CountryBar key={row.country} row={row} max={maxCountry} />
                  ))}
                </ul>
              )}
            </div>

            {/* ──────────── RECENT SIGNUPS ──────────── */}
            <div className="bd-surface p-5" data-testid="admin-analytics-recent">
              <div className="flex items-center gap-2 mb-3 font-bold text-lg">
                <Users size={16} /> Recent signups
              </div>
              <ul className="space-y-2">
                {data.recent.map((u) => (
                  <li
                    key={u.user_id}
                    className="flex items-center gap-3 p-2 rounded"
                    style={{ border: "1px solid var(--border)" }}
                    data-testid={`recent-user-${u.user_id}`}
                  >
                    <Avatar name={u.name || u.email} picture={u.picture} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{u.name || u.email}</div>
                      <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: "var(--text-dim)" }}>
                        {u.shipping_country && (
                          <span className="flex items-center gap-1"><MapPin size={10} /> {u.shipping_country}</span>
                        )}
                        {u.has_birthday && <span className="bd-chip" style={{ fontSize: "0.6rem" }}>Birthday</span>}
                        {u.has_avatar && <span className="bd-chip" style={{ fontSize: "0.6rem" }}>Photo</span>}
                        {u.created_at && <span className="bd-mono">{u.created_at.slice(0, 10)}</span>}
                      </div>
                    </div>
                  </li>
                ))}
                {data.recent.length === 0 && (
                  <li className="text-sm" style={{ color: "var(--text-dim)" }}>No signups yet.</li>
                )}
              </ul>
            </div>

            {/* ──────────── GROUPS FOLDER (per-group analytics drop-down) ──────────── */}
            <AdminGroupsFolder />
          </>
        )}
      </div>
    </AppShell>
  );
}
